'use client';
import { forwardRef, useImperativeHandle } from 'react';

import { useBreakpoint } from '@contexts/breakpoints';
import { useAppRouter } from '@contexts/router';
import { AirportSearchViewModel } from '@api/index';
import { FlexibleDiv, Row } from '@components/formStyles';
import { useFlightPastSearches } from '@hooks/useFlightPastSearches';
import useSearchBar from '@contexts/searchbar';
import { FlightSearchBarActionType } from '@contexts/searchbar/types/flight';
import { isDomesticFlight } from '@components/views/flight/utils';
import { FLIGHT_SEARCH_QUERY_PARAM_KEYS } from '@components/views/flight/constants';

import AirportSelection from './airportSelection';
import DateSelection from './dateSelection';
import FlightCabinClass from './flightCabinClass';
import FlightGuestBox from './flightGuestBox';
import TripSelection from './tripSelection';
import {
  FlightSearchBarWrapper,
  FlightSearchBarInnerWrapper,
  StyledContent,
  DatepickerWrapper,
  GuestBoxWrapper,
  ButtonWrapper,
  ListingGuestBox,
  ListingSearchbarWrapper,
  FlightSearchBarBodyWrapper,
} from './styled';
import DirectFlight from './directFlight';
import SearchFlightButton from './searchFlightButton';
import { formatDateForUrl } from '../utils/date';

type IFlightSearchBar = {
  isListingPage?: boolean;
  handleResearch?: () => void;
  alignVertical?: boolean;
  isTravelPlanning?: boolean;
};

export type IFlightSearchBarHandle = {
  triggerHandleSubmit: () => void;
};

const FlightSearchBar = forwardRef<IFlightSearchBarHandle, IFlightSearchBar>(
  (
    { isListingPage = false, handleResearch, alignVertical, isTravelPlanning },
    ref
  ) => {
    const { xl } = useBreakpoint();
    const {
      dispatch,
      state: { flight: state },
    } = useSearchBar();

    const router = useAppRouter();
    const {
      pastFlightItems,
      clearPastFlightSearch,
      removePastFlightSearch,
      saveFlightSearch,
    } = useFlightPastSearches();

    /**
     * This function is used to trigger the submit button from outside the component.
     * It is used in the drawer search bar in the hotel listing mobile header.
     */
    useImperativeHandle(ref, () => ({
      triggerHandleSubmit() {
        handleSubmit();
      },
    }));
    const checkFormState = () => {
      if (!state.arrivalAirport) {
        dispatch({
          type: FlightSearchBarActionType.SET_IS_ARRIVAL_ERROR,
          payload: true,
        });
      }
      if (!state.departureAirport) {
        dispatch({
          type: FlightSearchBarActionType.SET_IS_DEPARTURE_ERROR,
          payload: true,
        });
      }
      return !!state.arrivalAirport && !!state.departureAirport;
    };

    const handleSubmit = async () => {
      const isValid = checkFormState();
      if (!isValid) return;

      handleResearch?.();
      const pathname = isDomesticFlight(
        state?.departureAirport || undefined,
        state?.arrivalAirport || undefined
      )
        ? '/ic-hat-ucuslari'
        : '/dis-hat-ucuslari';

      let query = {
        ...(state.startDate && {
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureDate]: formatDateForUrl(
            state.startDate
          ),
        }),
        ...(state.endDate && {
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.returnDate]: formatDateForUrl(
            state.endDate
          ),
        }),

        ...(!state.endDate
          ? {
              [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay]: String(true),
            }
          : {
              [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isOneWay]: String(false),
            }),

        ...(state.isDirectFlight && {
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDirectFlight]: String(1),
        }),

        ...(state.guestInfo && {
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.adults]:
            state.guestInfo.numberOfAdultGuests,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.childs]:
            state.guestInfo.numberOfChildGuests,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.infants]:
            state.guestInfo.numberOfInfantGuests,
        }),
      };
      if (state.departureAirport) {
        query = {
          ...query,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureAirportCode]:
            state.departureAirport.airportCode,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic]: isDomesticFlight(
            state.departureAirport,
            state?.arrivalAirport || undefined
          )
            ? 'true'
            : 'false',
        };
        if (state.departureAirport?.isCity) {
          query = {
            ...query,
            [FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureIsCity]: 1,
          };
        } else {
          delete query[FLIGHT_SEARCH_QUERY_PARAM_KEYS.departureIsCity];
        }
      }
      if (state.arrivalAirport) {
        query = {
          ...query,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalAirportCode]:
            state.arrivalAirport.airportCode,
          [FLIGHT_SEARCH_QUERY_PARAM_KEYS.isDomestic]: isDomesticFlight(
            state.arrivalAirport,
            state?.departureAirport || undefined
          )
            ? 'true'
            : 'false',
        };
        if (state.arrivalAirport?.isCity) {
          query = {
            ...query,
            [FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalIsCity]: 1,
          };
        } else {
          delete query[FLIGHT_SEARCH_QUERY_PARAM_KEYS.arrivalIsCity];
        }
      }

      if (state.cabinClass === 'All') {
        delete query['out-classes'];
        delete query['in-classes'];
      } else if (state.isOneWay) {
        query = {
          ...query,
          'out-classes': state.cabinClass,
        };
      } else {
        query = {
          ...query,
          'out-classes': state.cabinClass,
          'in-classes': state.cabinClass,
        };
      }

      dispatch({
        type: FlightSearchBarActionType.SET_IS_SUBMIT,
        payload: false,
      });

      saveFlightSearch?.({
        arr: state.arrivalAirport as AirportSearchViewModel,
        dep: state.departureAirport as AirportSearchViewModel,
      });

      state.isDrawerOpen &&
        dispatch({
          type: FlightSearchBarActionType.SET_IS_DRAWER_OPEN,
          payload: false,
        });

      router.push({
        pathname,
        query,
      });
    };

    return (
      <FlightSearchBarWrapper isListingPage={isListingPage}>
        <FlightSearchBarInnerWrapper>
          <Row>
            <TripSelection />
            {xl && isListingPage && (
              <ListingSearchbarWrapper>
                <ListingGuestBox>
                  <FlightGuestBox isListingPage={isListingPage} />
                </ListingGuestBox>
                {!isListingPage && (
                  <FlexibleDiv>
                    <FlightCabinClass />
                  </FlexibleDiv>
                )}
              </ListingSearchbarWrapper>
            )}
            {xl && !isListingPage ? (
              <FlexibleDiv left={12}>
                <FlightCabinClass />
              </FlexibleDiv>
            ) : null}
          </Row>

          <FlightSearchBarBodyWrapper>
            <StyledContent $alignVertical={alignVertical}>
              <AirportSelection
                isListingPage={isListingPage}
                history={pastFlightItems}
                clearPastFlightSearch={clearPastFlightSearch}
                removePastFlightSearch={removePastFlightSearch}
              />

              <DatepickerWrapper $alignVertical={alignVertical}>
                <DateSelection isTravelPlanning={isTravelPlanning} />
              </DatepickerWrapper>

              {!xl || !isListingPage ? (
                <GuestBoxWrapper $alignVertical={alignVertical}>
                  <FlightGuestBox />
                </GuestBoxWrapper>
              ) : null}

              {!xl ? (
                <FlexibleDiv top={12}>
                  <FlightCabinClass />
                </FlexibleDiv>
              ) : null}

              {xl || xl === undefined || !isListingPage ? (
                <ButtonWrapper
                  $alignVertical={alignVertical}
                  isListingPage={isListingPage}
                >
                  <SearchFlightButton
                    handleResearch={handleSubmit}
                    isListingPage={isListingPage}
                  />
                </ButtonWrapper>
              ) : null}
            </StyledContent>
          </FlightSearchBarBodyWrapper>
          {alignVertical ? null : !isListingPage && (xl || xl === undefined) ? (
            <DirectFlight />
          ) : null}
        </FlightSearchBarInnerWrapper>
      </FlightSearchBarWrapper>
    );
  }
);

export default FlightSearchBar;
